import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { catchError, map, mergeMap, Observable, of } from 'rxjs';

import { EcmDispatcherModule } from '@ecm/dispatcher';
import { Contract, ContractsBusinessModel, PortfolioService, UrlHelper } from '@ecm/portfolio';
import { TMSService } from '@ecm/tracker';

import { ElcosService } from '../services/elcos.service';
import { ErrorPageService } from '../services/error-page.service';
import { RouterService } from '../services/router.service';

@Injectable({
    providedIn: 'root',
})
export class AuthGuard {
    /**
     * Class name for logger
     * @type {string}
     */
    private static CLASS_NAME = 'AuthGuard';

    /**
     * TMS property key for `statut_loggue`
     * @type {string}
     */
    private STATUT_LOGGUE_KEY = 'statut_loggue';

    /**
     * Is user authenticated
     * @type {boolean}
     */
    private isAuthenticated = false;

    public constructor(
        private tmsService: TMSService,
        private routerService: RouterService,
        private portfolioService: PortfolioService,
        private errorPageService: ErrorPageService,
        private elcosService: ElcosService,
    ) {}

    /**
     * Protects route to logged users
     * Performs a request to /contracts API through `PortfolioService` from `ecm-portfolio` module
     * -> if user is not logged, redirects to route.data.authGuardRedirect if set, login url otherwise
     * -> if user has only 'pro' or 'obs' contracts in his portfolio, redirects to related URL
     * -> else, performs identity check and sets context
     *
     * You can configure the route used when user is not logged by setting data.authGuardRedirect on the
     * route using this guard.
     * ```js
     * {
     *   path: 'logged',
     *   component: XXX,
     *   canActivate: [AuthGuard],
     *   data: {
     *     authGuardRedirect: '/visitor',
     *   },
     * }
     * ```
     * @param {ActivatedRouteSnapshot} route
     * @param {RouterStateSnapshot} state
     * @returns {Observable<boolean>}
     */
    public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        const url: string = state.url.toString();
        // if user already logged in, no need to perform identity check
        if (this.isAuthenticated) {
            EcmDispatcherModule.sendConsoleInfo(
                AuthGuard.CLASS_NAME,
                'User is already logged, will let go to route : ' + url,
            );
            return of(true);
        } else {
            // get portfolio to check user contracts type
            return this.portfolioService.getRawContracts().pipe(
                mergeMap((contractsBusinessModel: ContractsBusinessModel) => {
                    EcmDispatcherModule.sendConsoleInfo(
                        AuthGuard.CLASS_NAME,
                        'User is logged, will now check portfolio',
                    );

                    let hasPro = false;
                    let hasObs = false;
                    let hasGp = false;

                    // assign flags according to contract.telco.type property
                    contractsBusinessModel.contracts.forEach((contract: Contract) => {
                        switch (contract?.telco?.marketType?.toLowerCase?.()) {
                            case 'pro':
                                EcmDispatcherModule.sendConsoleInfo(
                                    AuthGuard.CLASS_NAME,
                                    "User has at least 1 'pro' contract",
                                );
                                hasPro = true;
                                break;
                            case 'obs':
                                EcmDispatcherModule.sendConsoleInfo(
                                    AuthGuard.CLASS_NAME,
                                    "User has at least 1 'obs' contract",
                                );
                                hasObs = true;
                                break;
                            default: // any other type is considered as GP contract
                                EcmDispatcherModule.sendConsoleInfo(
                                    AuthGuard.CLASS_NAME,
                                    'User has at least 1 GP contract',
                                );
                                hasGp = true;
                                break;
                        }
                    });

                    // user has GP contracts => stay on application and perform identity check
                    return hasGp ? this.handleGpUser(url) : this.handleNonGpUser(url, hasPro, hasObs);
                }),
                catchError((httpErrorResponse: HttpErrorResponse) => {
                    return this.handleError(url, httpErrorResponse, route.data?.authGuardRedirect);
                }),
            );
        }
    }

    /**
     * Check that children route are authorized
     * @param {ActivatedRouteSnapshot} route
     * @param {RouterStateSnapshot} state
     * @returns {Observable<boolean>}
     */
    public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
        return this.canActivate(route, state);
    }

    /**
     * Handles and allows Gp (Grand public) users
     * @param {string} url requested url
     * @returns {Observable<boolean>}
     */
    private handleGpUser(url: string): Observable<boolean> {
        // check if CID is present in route
        const regexMatches: string[] = UrlHelper.matchCidInPath(url);
        // use the first match only
        const cid: string = regexMatches?.[1] ?? null;
        EcmDispatcherModule.sendConsoleInfo(AuthGuard.CLASS_NAME, 'User is allowed, will let go to route : ' + url);

        this.tmsService.setProperty(this.STATUT_LOGGUE_KEY, 'oui');
        this.isAuthenticated = true;

        // dispatch currentContract
        if (cid) {
            // related cid contract will be dispatched by `PortfolioResolver`, so no need to dispatch from here
            return of(true);
        } else {
            // on transversal routes (without cid), dispatch loggedContract as currentContract
            return this.portfolioService.getLoggedContract().pipe(
                map((loggedContract: Contract) => {
                    if (loggedContract) {
                        this.routerService.storeAndDispatchCurrentContract(loggedContract);
                    }
                    return true;
                }),
            );
        }
    }

    /**
     * Handles Non-Gp users
     * Disallows full pro|obs portfolio users
     * Allows empty portfolio users
     * @param {string} url
     * @param {boolean} hasPro
     * @param {boolean} hasObs
     * @returns {Observable<boolean>}
     */
    private handleNonGpUser(url: string, hasPro: boolean, hasObs: boolean): Observable<boolean> {
        if (hasPro && hasObs) {
            EcmDispatcherModule.sendConsoleError(
                AuthGuard.CLASS_NAME,
                "User is 'pro' and 'obs', will not let go to route " + url,
            );
            return this.portfolioService.getLoggedContract().pipe(
                map((loggedContract: Contract) => {
                    const loggedContractType: string = loggedContract.telco.marketType.toLowerCase();
                    if (loggedContractType === 'pro') {
                        this.routerService.redirectToECPro();
                    } else if (loggedContractType === 'obs') {
                        this.routerService.redirectToOBL();
                    }
                    return false;
                }),
            );
        } else if (hasPro && !hasObs) {
            EcmDispatcherModule.sendConsoleError(
                AuthGuard.CLASS_NAME,
                "User is 'pro', will not let go to route " + url,
            );
            this.routerService.redirectToECPro();
            return of(false);
        } else if (hasObs && !hasPro) {
            EcmDispatcherModule.sendConsoleError(
                AuthGuard.CLASS_NAME,
                "User is 'obs', will not let go to route " + url,
            );
            this.routerService.redirectToOBL();
            return of(false);
        } else {
            // user has no contracts => stay on application
            EcmDispatcherModule.sendConsoleInfo(
                AuthGuard.CLASS_NAME,
                'User portfolio is empty, will let go to route ' + url,
            );
            this.tmsService.setProperty(this.STATUT_LOGGUE_KEY, 'oui');
            this.isAuthenticated = true;
            // set elcos
            this.elcosService.switchToOrange();
            return of(true);
        }
    }

    private handleError(url: string, httpErrorResponse: HttpErrorResponse, customRedirect: any): Observable<boolean> {
        EcmDispatcherModule.sendConsoleError(AuthGuard.CLASS_NAME, 'Problem during authentication check');
        if (httpErrorResponse.status !== undefined) {
            const status: string = httpErrorResponse.status.toString();
            EcmDispatcherModule.sendConsoleError(AuthGuard.CLASS_NAME, 'HTTP error status : ' + status);
            if (status === '401' || status === '403') {
                EcmDispatcherModule.sendConsoleError(
                    AuthGuard.CLASS_NAME,
                    'User is not logged, will not go to route : ' + url,
                );
                EcmDispatcherModule.sendConsoleError(AuthGuard.CLASS_NAME, 'Redirecting to Orange login page ...');
                if (customRedirect) {
                    this.routerService.navigate(customRedirect);
                } else {
                    this.routerService.redirectToAuthenticationPage();
                }
            } else {
                EcmDispatcherModule.sendConsoleError(
                    AuthGuard.CLASS_NAME,
                    'Unknown error, will show default error page',
                );
                this.errorPageService.showDefaultErrorPage();
            }
        } else {
            EcmDispatcherModule.sendConsoleError(AuthGuard.CLASS_NAME, 'No status in HttpErrorResponse object');
        }
        // set not-logged status in TMS
        this.tmsService.setProperty(this.STATUT_LOGGUE_KEY, 'non');
        return of(false);
    }
}
